import React, { useEffect, useRef, useState } from "react";
import { Save, Upload, RefreshCw, Image as ImageIcon } from "lucide-react";
import { api, adminUploadImage, resolveImage } from "../../lib/api";
import { useSiteSettings, updateSiteSettings } from "../../lib/siteSettings";
import { toast } from "sonner";

const COVER_FIELDS = [
  { key: "cover_outsourcing", label: "Capa — Outsourcing", page: "/outsourcing" },
  { key: "cover_produtos", label: "Capa — Produtos", page: "/produtos" },
  { key: "cover_sobre", label: "Capa — Quem Somos", page: "/sobre" },
  { key: "cover_contato", label: "Capa — Contato", page: "/contato" },
];

export default function AdminSiteSettings() {
  const { settings, refresh } = useSiteSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  // Sync local state when context updates
  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await updateSiteSettings(form);
      await refresh();
      toast.success("Conteúdo do site atualizado!");
    } catch (err) {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (window.confirm("Restaurar valores atuais (descartar alterações não salvas)?")) {
      setForm(settings);
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-site-settings-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-3">// CONTEÚDO DO SITE</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Capas & textos da home</h1>
          <p className="mt-2 text-sm text-[#6B7280]">
            Edite a imagem de capa de cada página e os textos da primeira dobra da home.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onReset}
            data-testid="admin-site-reset"
            className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-[#1A1A1A] text-xs font-bold uppercase tracking-[0.18em] px-5 py-3"
          >
            <RefreshCw size={14} /> Descartar
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="admin-site-save"
            className="inline-flex items-center gap-2 bg-[#E84E34] text-white font-bold px-6 py-3 uppercase text-xs tracking-[0.18em] hover:bg-[#CF412A] disabled:opacity-60"
          >
            {saving ? "Salvando..." : "Salvar tudo"} <Save size={14} />
          </button>
        </div>
      </div>

      {/* HOME HERO */}
      <Section title="Home — Primeira dobra (hero)" testid="section-home">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Overline (linha pequena acima do título)">
            <Input value={form.home_overline} onChange={(v) => update("home_overline", v)} testid="field-home-overline" />
          </Field>
          <Field label="Botão primário — texto">
            <Input value={form.home_cta_primary_text} onChange={(v) => update("home_cta_primary_text", v)} testid="field-home-cta-text" />
          </Field>
          <Field label="Título principal (linha branca)">
            <Input value={form.home_title_main} onChange={(v) => update("home_title_main", v)} testid="field-home-title-main" />
          </Field>
          <Field label="Título destaque (linha laranja)">
            <Input value={form.home_title_accent} onChange={(v) => update("home_title_accent", v)} testid="field-home-title-accent" />
          </Field>
          <Field label="Subtítulo" full>
            <Textarea value={form.home_subtitle} onChange={(v) => update("home_subtitle", v)} testid="field-home-subtitle" rows={3} />
          </Field>
          <Field label="Botão primário — mensagem do WhatsApp" full>
            <Input value={form.home_cta_primary_message} onChange={(v) => update("home_cta_primary_message", v)} testid="field-home-cta-message" />
          </Field>
          <Field label="Botão secundário — texto">
            <Input value={form.home_cta_secondary_text} onChange={(v) => update("home_cta_secondary_text", v)} testid="field-home-cta-secondary" />
          </Field>
        </div>

        <div className="mt-6">
          <ImageField
            label="Imagem de fundo da hero"
            value={form.home_hero_image}
            onChange={(v) => update("home_hero_image", v)}
            testid="field-home-hero-image"
          />
        </div>
      </Section>

      {/* PAGE COVERS */}
      <Section title="Capas das páginas internas" testid="section-covers">
        <div className="space-y-6">
          {COVER_FIELDS.map((c) => (
            <ImageField
              key={c.key}
              label={c.label}
              hint={c.page}
              value={form[c.key]}
              onChange={(v) => update(c.key, v)}
              testid={`field-${c.key}`}
            />
          ))}
        </div>
      </Section>

      <div className="sticky bottom-0 mt-10 -mx-8 md:-mx-12 px-8 md:px-12 py-4 bg-white/95 backdrop-blur border-t border-[#E5E7EB] flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#E84E34] text-white font-bold px-7 py-3.5 uppercase text-xs tracking-[0.18em] hover:bg-[#CF412A] disabled:opacity-60"
        >
          {saving ? "Salvando..." : "Salvar tudo"} <Save size={14} />
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, children, testid }) => (
  <div className="mb-10 bg-white border border-[#E5E7EB] p-6 md:p-8" data-testid={testid}>
    <h2 className="font-display text-xl md:text-2xl font-bold border-b border-[#E5E7EB] pb-4 mb-6">
      {title}
    </h2>
    {children}
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mb-2">
      {label}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, testid }) => (
  <input
    type="text"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
  />
);

const Textarea = ({ value, onChange, testid, rows = 3 }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    data-testid={testid}
    className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none resize-none"
  />
);

const ImageField = ({ label, hint, value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUploadImage(file);
      onChange(url);
      toast.success("Imagem enviada!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha no upload";
      toast.error(typeof msg === "string" ? msg : "Falha no upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2">
        <label className="block font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280]">
          {label}
        </label>
        {hint && <span className="text-[10px] text-[#9CA3AF] font-mono-sm">{hint}</span>}
      </div>
      <div className="grid md:grid-cols-[180px_1fr] gap-4 items-start">
        <div className="w-full aspect-[4/3] bg-[#F9F9F9] border border-[#E5E7EB] overflow-hidden">
          {value ? (
            <img
              src={resolveImage(value)}
              alt={label}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]">
              <ImageIcon size={24} />
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid}-upload`}
              className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] hover:bg-[#E84E34] transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              <Upload size={14} />
              {uploading ? "Enviando..." : "Enviar imagem"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
          </div>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="ou cole uma URL externa"
            className="w-full border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
          />
        </div>
      </div>
    </div>
  );
};
